import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Disables the button and swaps the leading icon for a spinner. */
  loading?: boolean;
  /** Replaces the label while loading, e.g. "Entrando…". */
  loadingLabel?: string;
  icon?: LucideIcon;
  iconRight?: LucideIcon;
  /** Square button with no label. `aria-label` becomes mandatory in practice. */
  iconOnly?: boolean;
  fullWidth?: boolean;
  children?: ReactNode;
}

const VARIANT: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-on-primary border-primary hover:bg-primary-hover hover:border-primary-hover',
  secondary: 'bg-surface-2 text-text border-border hover:bg-surface-3 hover:border-border-strong',
  ghost: 'bg-transparent text-text-muted border-transparent hover:bg-surface-2 hover:text-text',
  danger: 'bg-loss-dim text-loss border-loss/40 hover:bg-loss hover:text-bg hover:border-loss',
};

const SIZE: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm gap-1.5 rounded-sm',
  md: 'h-11 px-4 text-base gap-2 rounded-md',
  lg: 'h-13 px-6 text-lg gap-2 rounded-md',
};

const ICON_ONLY_SIZE: Record<ButtonSize, string> = {
  sm: 'h-9 w-9 px-0',
  md: 'h-11 w-11 px-0',
  lg: 'h-13 w-13 px-0',
};

const ICON_SIZE: Record<ButtonSize, string> = {
  sm: 'size-4',
  md: 'size-[18px]',
  lg: 'size-5',
};

/**
 * `sm` is 36px tall so it fits a 36px table row, but the pointer target is grown
 * to 44x44 with a transparent pseudo-element that costs no layout space.
 */
const HIT_SLOP =
  "after:absolute after:left-1/2 after:top-1/2 after:h-11 after:w-full after:min-w-11 after:-translate-x-1/2 after:-translate-y-1/2 after:content-['']";

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    loadingLabel,
    icon: Icon,
    iconRight: IconRight,
    iconOnly = false,
    fullWidth = false,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = disabled || loading;
  const spinnerSize = size === 'lg' ? 'md' : 'sm';

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(
        'relative inline-flex items-center justify-center border font-semibold',
        'transition-colors duration-150 ease-out-soft',
        'disabled:cursor-not-allowed disabled:opacity-50',
        VARIANT[variant],
        SIZE[size],
        iconOnly && ICON_ONLY_SIZE[size],
        size === 'sm' && HIT_SLOP,
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Spinner size={spinnerSize} label={null} />
      ) : Icon ? (
        <Icon aria-hidden="true" className={cn('shrink-0', ICON_SIZE[size])} />
      ) : null}

      {!iconOnly && (loading && loadingLabel ? <span>{loadingLabel}</span> : children)}

      {!loading && IconRight ? (
        <IconRight aria-hidden="true" className={cn('shrink-0', ICON_SIZE[size])} />
      ) : null}
    </button>
  );
});
